import './AttachmentsList.module.css';

import { MoreHorizontal20Regular } from '@fluentui/react-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Attachment } from '../Attachment/Attachment';
import { Button } from '../Button/Button';
import { formatBytes } from '../FileUtils/formatBytes';
import { getFileIcon } from '../FileUtils/getFileIcon';
import { Popup } from '../Popup/Popup';
import {
  AttachmentBackgroundColor,
  AttachmentItem,
  AttachmentMenuItem,
  AttachmentsListProps,
} from './AttachmentsList.interface';

interface AttachmentMenuProps {
  menu: AttachmentMenuItem[];
  attachmentItem: AttachmentItem;
}

const AttachmentMenu: React.FC<AttachmentMenuProps> = ({ menu, attachmentItem }) => {
  const { t } = useTranslation();

  return (
    <Popup
      trapFocus
      trigger={
        <Button
          appearance="transparent"
          icon={<MoreHorizontal20Regular />}
          aria-haspopup="true"
          aria-controls="attachmentMenu"
          aria-label={t('more_options', { defaultValue: 'Mehr Optionen' })}
          data-testid="AttachmentItemMenuTrigger"
        />
      }
      content={
        <div id="attachmentMenu" data-testid="AttachmentMenu" className="flex flex-col popup">
          {menu.map((item) => (
            <Button
              key={item.title}
              appearance="transparent"
              icon={item.icon}
              onClick={() => {
                if (typeof item.handler === 'function') {
                  item.handler(attachmentItem);
                }
              }}
            >
              {item.title}
            </Button>
          ))}
        </div>
      }
    />
  );
};

const getItemDescription = (attachmentItem: AttachmentItem) => {
  const { size } = attachmentItem;
  return size ? `${formatBytes(size)} ` : '';
};

const AttachmentsList: React.FC<AttachmentsListProps> = ({
  attachments,
  menu = [],
  bgColor,
  className = AttachmentBackgroundColor.Grey,
  attachmentClick,
}) => {
  return (
    <>
      {attachments.map((attachmentItem) => (
        <Attachment
          data-testid="AttachmentItem"
          key={attachmentItem.id}
          header={attachmentItem.name || ''}
          className={`attachmentItem${
            bgColor === AttachmentBackgroundColor.White ? ' bgWhite' : ''
          }${className ? ' ' + className : ''}`}
          icon={getFileIcon(attachmentItem.contentType || 'application/octet-stream')}
          action={
            menu.length > 0 ? (
              <AttachmentMenu menu={menu} attachmentItem={attachmentItem} />
            ) : undefined
          }
          description={getItemDescription(attachmentItem)}
          onClick={() => {
            if (attachmentClick) {
              attachmentClick(attachmentItem);
            }
          }}
        />
      ))}
    </>
  );
};

export default AttachmentsList;
